import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Stack,
  Chip,
  Grid,
  Divider,
  Menu,
  Link,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  OpenInNew as OpenInNewIcon,
  ArrowDropDown as ArrowDropDownIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import moment from 'moment';
import api from '../services/api';

// ---- Interfaces ----

interface DaigouItem {
  url: string;
  name: string;
  image: string;
  category: string;
  weight_kg: number;
  item_krw: number;
  item_twd: number;
  quantity?: number;
  spec?: string | null;
  line_twd?: number | null;
  price_source: string;
}

interface DaigouOrder {
  order_id: string;
  source_url: string | null;
  product_name: string | null;
  product_image: string | null;
  items?: DaigouItem[] | null;
  item_krw: number | null;
  quoted_total: number | null;
  customer_name: string | null;
  customer_phone: string | null;
  customer_email: string | null;
  delivery_method: string | null;
  store_data: any;
  shipping_address: string | null;
  options_note: string | null;
  payment_method: string | null;
  payment_no: string | null;
  price_source: 'scraped' | 'manual' | null;
  status: string;
  created_at: string | null;
  paid_at: string | null;
}

// ---- Status lifecycle + labels ----

const STATUS_LABELS: Record<string, string> = {
  PENDING_PAYMENT: '待付款',
  PAID: '已付款',
  PURCHASING: '韓國採購中',
  ARRIVED: '已到貨',
  SHIPPED: '已寄出',
  COMPLETED: '完成',
  CANCELLED: '已取消',
};

// Ordered list of statuses for the 更改狀態 menu and filter dropdown
const STATUS_OPTIONS = [
  'PENDING_PAYMENT',
  'PAID',
  'PURCHASING',
  'ARRIVED',
  'SHIPPED',
  'COMPLETED',
  'CANCELLED',
];

type ChipColor = 'default' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning';

const STATUS_COLORS: Record<string, ChipColor> = {
  PENDING_PAYMENT: 'warning',
  PAID: 'info',
  PURCHASING: 'secondary',
  ARRIVED: 'primary',
  SHIPPED: 'info',
  COMPLETED: 'success',
  CANCELLED: 'error',
};

// Map delivery_method codes to readable labels
const DELIVERY_LABELS: Record<string, string> = {
  pickup: '超商取貨',
  shipping: '宅配',
  home_delivery: '宅配',
};

// ---- Component ----

const DaigouOrdersPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();

  const [orders, setOrders] = useState<DaigouOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Status-change menu
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<null | HTMLElement>(null);
  const [statusMenuOrderId, setStatusMenuOrderId] = useState<string>('');

  // ---- Data fetching ----
  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = { limit: '500' };
      if (statusFilter !== 'all') params.status = statusFilter;
      const res = await api.get('/api/daigou/orders', { params });
      setOrders(res.data.orders || []);
    } catch (err: any) {
      enqueueSnackbar(`載入代購訂單失敗: ${err.message}`, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [statusFilter, enqueueSnackbar]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // ---- Handlers ----
  const openStatusMenu = (event: React.MouseEvent<HTMLElement>, orderId: string) => {
    setStatusMenuAnchor(event.currentTarget);
    setStatusMenuOrderId(orderId);
  };

  const closeStatusMenu = () => {
    setStatusMenuAnchor(null);
    setStatusMenuOrderId('');
  };

  const handleStatusChange = async (orderId: string, newStatus: string) => {
    try {
      await api.patch(`/api/daigou/orders/${orderId}/status`, { status: newStatus });
      setOrders(prev => prev.map(o => (o.order_id === orderId ? { ...o, status: newStatus } : o)));
      enqueueSnackbar(`${orderId} → ${STATUS_LABELS[newStatus] || newStatus}`, { variant: 'success' });
    } catch (err: any) {
      enqueueSnackbar(`更新狀態失敗: ${err.message}`, { variant: 'error' });
    }
    closeStatusMenu();
  };

  // Resolve store/address display from delivery_method + store_data + shipping_address
  const getDeliveryDisplay = (order: DaigouOrder): string => {
    const method = DELIVERY_LABELS[order.delivery_method || ''] || order.delivery_method || '—';
    const store = order.store_data || {};
    const storeName = store.store_name || store.storeName || store.name;
    const storeAddr = store.store_address || store.address;
    if (storeName || storeAddr) {
      return [method, storeName, storeAddr].filter(Boolean).join(' · ');
    }
    if (order.shipping_address) {
      return [method, order.shipping_address].filter(Boolean).join(' · ');
    }
    return method;
  };

  const fmtDate = (d: string | null) => (d ? moment(d).format('YYYY/MM/DD HH:mm') : '—');
  const fmtMoney = (n: number | null) => (n != null ? `NT$ ${Number(n).toLocaleString()}` : '—');

  // ---- Render ----
  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          flexWrap: 'wrap',
          gap: 1.5,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            代購訂單
          </Typography>
          <Typography variant="caption" color="text.secondary">
            標示「待核價」的訂單為客戶自行輸入售價，採購前請先核對韓國實際售價。
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="daigou-status-filter-label">狀態</InputLabel>
            <Select
              labelId="daigou-status-filter-label"
              label="狀態"
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">全部</MenuItem>
              {STATUS_OPTIONS.map(s => (
                <MenuItem key={s} value={s}>
                  {STATUS_LABELS[s]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchOrders} disabled={loading}>
            重新整理
          </Button>
        </Stack>
      </Box>

      {/* Body */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : orders.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
          <Typography>沒有代購訂單</Typography>
        </Box>
      ) : (
        <Stack spacing={2}>
          {orders.map(order => {
            const hasItems = Array.isArray(order.items) && order.items.length > 0;
            return (
            <Card key={order.order_id} variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Grid container spacing={2}>
                  {/* Product image (single-item fallback only) */}
                  {!hasItems && (
                  <Grid item xs={12} sm="auto">
                    {order.product_image ? (
                      <Box
                        component="img"
                        src={order.product_image}
                        alt={order.product_name || ''}
                        sx={{
                          width: 96,
                          height: 96,
                          objectFit: 'cover',
                          borderRadius: 1.5,
                          border: '1px solid #e0e0e0',
                          bgcolor: '#fafafa',
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: 96,
                          height: 96,
                          borderRadius: 1.5,
                          border: '1px solid #e0e0e0',
                          bgcolor: '#fafafa',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'text.disabled',
                          fontSize: '0.75rem',
                        }}
                      >
                        無圖片
                      </Box>
                    )}
                  </Grid>
                  )}

                  {/* Details */}
                  <Grid item xs={12} sm>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      spacing={1}
                      flexWrap="wrap"
                    >
                      <Box sx={{ minWidth: 0 }}>
                        {hasItems ? (
                          <Typography sx={{ fontWeight: 700, fontSize: '1.05rem' }}>
                            {`商品 (${order.items!.length} 件)`}
                          </Typography>
                        ) : order.source_url ? (
                          <Link
                            href={order.source_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                            sx={{ fontWeight: 700, fontSize: '1.05rem', display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                          >
                            {order.product_name || '(未命名商品)'}
                            <OpenInNewIcon sx={{ fontSize: '1rem' }} />
                          </Link>
                        ) : (
                          <Typography sx={{ fontWeight: 700, fontSize: '1.05rem' }}>
                            {order.product_name || '(未命名商品)'}
                          </Typography>
                        )}
                        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                          {order.order_id}
                        </Typography>
                      </Box>

                      {/* Status chip + 更改狀態 menu */}
                      <Stack direction="row" spacing={1} alignItems="center">
                        {/* Manual-priced orders need the real KR price verified before purchasing */}
                        {order.price_source === 'manual' && (
                          <Chip
                            label="待核價"
                            color="warning"
                            size="small"
                            sx={{ fontWeight: 700 }}
                          />
                        )}
                        <Chip
                          label={STATUS_LABELS[order.status] || order.status}
                          color={STATUS_COLORS[order.status] || 'default'}
                          size="small"
                          sx={{ fontWeight: 700 }}
                        />
                        <Button
                          size="small"
                          variant="outlined"
                          endIcon={<ArrowDropDownIcon />}
                          onClick={e => openStatusMenu(e, order.order_id)}
                        >
                          更改狀態
                        </Button>
                      </Stack>
                    </Stack>

                    <Divider sx={{ my: 1.5 }} />

                    {/* Multi-item list (new orders with items[] JSONB) */}
                    {hasItems && (
                      <Stack spacing={1} sx={{ mb: 1.5 }}>
                        {order.items!.map((item, idx) => (
                          <Stack
                            key={idx}
                            direction="row"
                            spacing={1.25}
                            alignItems="center"
                            sx={{
                              p: 1,
                              borderRadius: 1.5,
                              border: '1px solid #eeeeee',
                              bgcolor: '#fafafa',
                            }}
                          >
                            {item.image ? (
                              <Box
                                component="img"
                                src={item.image}
                                alt={item.name || ''}
                                sx={{
                                  width: 48,
                                  height: 48,
                                  flexShrink: 0,
                                  objectFit: 'cover',
                                  borderRadius: 1,
                                  border: '1px solid #e0e0e0',
                                  bgcolor: '#fff',
                                }}
                              />
                            ) : (
                              <Box
                                sx={{
                                  width: 48,
                                  height: 48,
                                  flexShrink: 0,
                                  borderRadius: 1,
                                  border: '1px solid #e0e0e0',
                                  bgcolor: '#fff',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: 'text.disabled',
                                  fontSize: '0.65rem',
                                }}
                              >
                                無圖
                              </Box>
                            )}
                            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                                {item.url ? (
                                  <Link
                                    href={item.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    underline="hover"
                                    sx={{
                                      fontWeight: 600,
                                      fontSize: '0.9rem',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: 0.5,
                                    }}
                                  >
                                    {item.name || '(未命名商品)'}
                                    <OpenInNewIcon sx={{ fontSize: '0.85rem' }} />
                                  </Link>
                                ) : (
                                  <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                                    {item.name || '(未命名商品)'}
                                  </Typography>
                                )}
                                {/* Quantity is easy to miss but affects what gets purchased — make it pop */}
                                {(item.quantity ?? 1) > 1 && (
                                  <Chip
                                    label={`×${item.quantity}`}
                                    color="error"
                                    size="small"
                                    sx={{ height: 20, fontWeight: 800, fontSize: '0.72rem' }}
                                  />
                                )}
                              </Stack>
                              {/* Buyer instructions — what to actually pick when purchasing in Korea */}
                              {(item.spec || (item.quantity ?? 1) > 1) && (
                                <Typography
                                  variant="caption"
                                  sx={{ display: 'block', mt: 0.25, color: 'text.secondary', fontWeight: 600 }}
                                >
                                  {[
                                    item.spec ? `規格：${item.spec}` : null,
                                    `數量：${item.quantity ?? 1}`,
                                  ]
                                    .filter(Boolean)
                                    .join('　')}
                                </Typography>
                              )}
                            </Box>
                            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexShrink: 0 }}>
                              {item.price_source === 'manual' && (
                                <Chip label="待核價" color="warning" size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
                              )}
                              <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                                {item.item_krw != null ? `₩${Number(item.item_krw).toLocaleString()}` : '—'}
                              </Typography>
                            </Stack>
                          </Stack>
                        ))}
                      </Stack>
                    )}

                    {/* 規格備註 — prominent (size/color the customer wants) */}
                    <Box
                      sx={{
                        mb: 1.5,
                        p: 1.25,
                        borderRadius: 1.5,
                        bgcolor: '#fff8e1',
                        border: '1px solid #ffe082',
                      }}
                    >
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#8d6e00' }}>
                        規格備註
                      </Typography>
                      <Typography sx={{ fontWeight: 600, whiteSpace: 'pre-wrap' }}>
                        {order.options_note || '（無）'}
                      </Typography>
                    </Box>

                    <Grid container spacing={1.5}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">
                          客戶
                        </Typography>
                        <Typography variant="body2">
                          {order.customer_name || '—'}
                          {order.customer_phone ? ` · ${order.customer_phone}` : ''}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">
                          配送方式
                        </Typography>
                        <Typography variant="body2">{getDeliveryDisplay(order)}</Typography>
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <Typography variant="caption" color="text.secondary">
                          報價總額
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {fmtMoney(order.quoted_total)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <Typography variant="caption" color="text.secondary">
                          韓國售價
                        </Typography>
                        <Typography variant="body2">
                          {order.item_krw != null ? `₩ ${Number(order.item_krw).toLocaleString()}` : '—'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="caption" color="text.secondary">
                          付款方式
                        </Typography>
                        <Typography variant="body2">
                          {order.payment_method || '—'}
                          {order.payment_no ? ` · ${order.payment_no}` : ''}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={6}>
                        <Typography variant="caption" color="text.secondary">
                          建立時間
                        </Typography>
                        <Typography variant="body2">{fmtDate(order.created_at)}</Typography>
                      </Grid>
                      <Grid item xs={6} sm={6}>
                        <Typography variant="caption" color="text.secondary">
                          付款時間
                        </Typography>
                        <Typography variant="body2">{fmtDate(order.paid_at)}</Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
            );
          })}
        </Stack>
      )}

      {/* Shared status-change menu */}
      <Menu anchorEl={statusMenuAnchor} open={Boolean(statusMenuAnchor)} onClose={closeStatusMenu}>
        {STATUS_OPTIONS.map(s => (
          <MenuItem key={s} onClick={() => handleStatusChange(statusMenuOrderId, s)}>
            {STATUS_LABELS[s]}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default DaigouOrdersPage;
